import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, resolvePhoto } from "@/lib/api";
import { toast } from "sonner";
import { motion, Reorder } from "framer-motion";
import { Sparkles, Upload, X, Loader2, ArrowRight, GripVertical } from "lucide-react";

const GENDER_OPTS = [
  { v: "woman", label: "Woman" },
  { v: "man", label: "Man" },
  { v: "nonbinary", label: "Nonbinary" },
];

const INTEREST_SUGGESTIONS = [
  "Books", "Cinema", "Hiking", "Cooking", "Vinyl", "Travel", "Coffee",
  "Photography", "Yoga", "Cycling", "Art", "Music", "Dance", "Gardening",
  "Writing", "Wine", "Gaming", "Theatre",
];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState(1);
  const [name, setName] = useState(user?.name || "");
  const [age, setAge] = useState(user?.age || 25);
  const [gender, setGender] = useState(user?.gender || "");
  const [interestedIn, setInterestedIn] = useState(user?.interested_in || []);
  const [bio, setBio] = useState(user?.bio || "");
  const [interests, setInterests] = useState(user?.interests || []);
  const [location, setLocation] = useState(user?.location || "");
  const [photos, setPhotos] = useState(user?.photos || []);
  const [polishing, setPolishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggleInterestedIn = (v) => {
    setInterestedIn((prev) => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  };
  const toggleInterest = (v) => {
    setInterests((prev) => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/me/photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((p) => [...p, data.photo_url]);
      toast.success("Photo added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const polishBio = async () => {
    if (!bio.trim()) {
      toast.error("Write a draft first, then Claude can polish it");
      return;
    }
    setPolishing(true);
    try {
      const { data } = await api.post("/ai/bio-polish", { bio });
      setBio(data.polished_bio);
      toast.success("Bio refined by Claude");
    } catch (err) {
      toast.error("AI editor is resting. Try again.");
    } finally {
      setPolishing(false);
    }
  };

  const finish = async () => {
    if (!gender || interestedIn.length === 0 || !age || photos.length === 0) {
      toast.error("Add a photo, age, gender and at least one interest preference");
      return;
    }
    setSaving(true);
    try {
      await api.put("/me", {
        name, age: Number(age), gender, interested_in: interestedIn,
        bio, interests, location, photos, onboarded: true,
      });
      await refresh();
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen grain-bg flex flex-col" data-testid="onboarding-page">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between">
        <span className="font-serif italic text-2xl text-[#913831]">rendezvous</span>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373]">
          Step <span className="font-serif text-[#1A1818] text-lg not-italic">0{step}</span> / 03
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 sm:px-10 pb-20">
        <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          {step === 1 && (
            <section>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1A1818] leading-tight">
                The basics.<br /><em className="text-[#913831]">Briefly.</em>
              </h1>
              <p className="mt-3 text-[#737373]">We only ask for what we need to introduce you well.</p>

              <div className="mt-10 space-y-8">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#737373]">Name</label>
                  <input data-testid="ob-name" value={name} onChange={(e) => setName(e.target.value)}
                    className="mt-2 w-full bg-transparent border-b border-[#E5DFD3] focus:border-[#913831] focus:outline-none py-2 text-2xl font-serif" />
                </div>
                <div className="flex gap-6">
                  <div className="flex-1">
                    <label className="text-xs uppercase tracking-[0.2em] text-[#737373]">Age</label>
                    <input data-testid="ob-age" type="number" min="18" max="99" value={age}
                      onChange={(e) => setAge(e.target.value)}
                      className="mt-2 w-full bg-transparent border-b border-[#E5DFD3] focus:border-[#913831] focus:outline-none py-2 text-2xl font-serif" />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs uppercase tracking-[0.2em] text-[#737373]">City</label>
                    <input data-testid="ob-location" value={location} onChange={(e) => setLocation(e.target.value)}
                      placeholder="Lisbon"
                      className="mt-2 w-full bg-transparent border-b border-[#E5DFD3] focus:border-[#913831] focus:outline-none py-2 text-2xl font-serif" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#737373]">I am a</label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {GENDER_OPTS.map(o => (
                      <button key={o.v} data-testid={`ob-gender-${o.v}`} type="button"
                        onClick={() => setGender(o.v)}
                        className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all ${
                          gender === o.v
                            ? "bg-[#913831] text-white border-[#913831]"
                            : "bg-transparent text-[#1A1818] border-[#E5DFD3] hover:bg-[#F0EBE1]"
                        }`}
                      >{o.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#737373]">Interested in</label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {GENDER_OPTS.map(o => (
                      <button key={o.v} data-testid={`ob-interested-${o.v}`} type="button"
                        onClick={() => toggleInterestedIn(o.v)}
                        className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all ${
                          interestedIn.includes(o.v)
                            ? "bg-[#913831] text-white border-[#913831]"
                            : "bg-transparent text-[#1A1818] border-[#E5DFD3] hover:bg-[#F0EBE1]"
                        }`}
                      >{o.label}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-12 flex justify-end">
                <button data-testid="ob-next-1" onClick={() => setStep(2)}
                  disabled={!name || !age || !gender || interestedIn.length === 0}
                  className="inline-flex items-center gap-2 bg-[#913831] hover:bg-[#7A2E28] disabled:opacity-50 text-white rounded-full px-7 py-3.5 font-medium">
                  Continue <ArrowRight size={18} />
                </button>
              </div>
            </section>
          )}

          {step === 2 && (
            <section>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1A1818] leading-tight">
                A small <em className="text-[#913831]">self-portrait</em>.
              </h1>
              <p className="mt-3 text-[#737373]">Two or three sentences is plenty. Claude can help you sound like yourself.</p>

              <div className="mt-8">
                <textarea
                  data-testid="ob-bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  rows={6}
                  placeholder="I bake bread on Sundays, take wrong turns on purpose, and want to fall in love with someone who has at least one weird, specific obsession."
                  className="w-full bg-white border border-[#E5DFD3] rounded-2xl p-5 font-sans text-base focus:outline-none focus:border-[#913831] resize-none"
                />
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-[#737373]">{bio.length}/600</span>
                  <button data-testid="ob-polish" onClick={polishBio} disabled={polishing}
                    className="inline-flex items-center gap-2 bg-[#F3E8DD] hover:bg-[#EADBC8] text-[#1A1818] rounded-full px-5 py-2.5 text-sm font-medium border border-[#D4A373]/40 disabled:opacity-50">
                    {polishing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} className="text-[#913831]" />}
                    {polishing ? <span className="ai-shimmer font-medium">Refining…</span> : "Refine with Claude"}
                  </button>
                </div>
              </div>

              <div className="mt-10">
                <label className="text-xs uppercase tracking-[0.2em] text-[#737373]">Interests</label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {INTEREST_SUGGESTIONS.map(t => (
                    <button key={t} data-testid={`ob-interest-${t}`} type="button" onClick={() => toggleInterest(t)}
                      className={`px-4 py-2 rounded-full text-sm border transition-all ${
                        interests.includes(t)
                          ? "bg-[#1A1818] text-white border-[#1A1818]"
                          : "bg-transparent text-[#1A1818] border-[#E5DFD3] hover:bg-[#F0EBE1]"
                      }`}
                    >{t}</button>
                  ))}
                </div>
              </div>

              <div className="mt-12 flex justify-between">
                <button data-testid="ob-back-2" onClick={() => setStep(1)}
                  className="text-[#737373] hover:text-[#1A1818] text-sm">← Back</button>
                <button data-testid="ob-next-2" onClick={() => setStep(3)}
                  className="inline-flex items-center gap-2 bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full px-7 py-3.5 font-medium">
                  Continue <ArrowRight size={18} />
                </button>
              </div>
            </section>
          )}

          {step === 3 && (
            <section>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1A1818] leading-tight">
                One photo to <em className="text-[#913831]">begin</em>.
              </h1>
              <p className="mt-3 text-[#737373]">Pick one where you look like yourself. Drag to reorder — the first becomes your main photo.</p>

              <Reorder.Group axis="y" values={photos} onReorder={setPhotos}
                className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4" as="div" data-testid="ob-photos-grid">
                {photos.map((p, i) => (
                  <Reorder.Item key={p} value={p} as="div"
                    className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-[#F0EBE1] border border-[#E5DFD3] cursor-grab active:cursor-grabbing"
                    data-testid={`ob-photo-${i}`}
                    whileDrag={{ scale: 1.04, zIndex: 5, boxShadow: "0 20px 40px -10px rgba(0,0,0,0.25)" }}
                  >
                    <img src={resolvePhoto(p)} alt="" className="w-full h-full object-cover pointer-events-none" />
                    {i === 0 && (
                      <span className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.18em] bg-[#913831] text-white px-2 py-0.5 rounded-full">
                        Main
                      </span>
                    )}
                    <div className="absolute bottom-2 left-2 w-7 h-7 bg-white/85 backdrop-blur rounded-full flex items-center justify-center pointer-events-none">
                      <GripVertical size={14} className="text-[#737373]" />
                    </div>
                    <button data-testid={`ob-remove-photo-${i}`} type="button"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => setPhotos(photos.filter((x) => x !== p))}
                      className="absolute top-2 right-2 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center hover:bg-white">
                      <X size={14} />
                    </button>
                  </Reorder.Item>
                ))}
                {photos.length < 6 && (
                  <label className="aspect-[3/4] rounded-2xl border-2 border-dashed border-[#E5DFD3] flex flex-col items-center justify-center cursor-pointer hover:border-[#913831] hover:bg-[#F0EBE1]/40 transition-colors text-[#737373]"
                         data-testid="ob-upload-zone">
                    <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadPhoto}
                      data-testid="ob-photo-input" />
                    {uploading
                      ? <Loader2 size={20} className="animate-spin" />
                      : <><Upload size={22} strokeWidth={1.5} /><span className="text-xs uppercase tracking-[0.2em] mt-2">Add photo</span></>}
                  </label>
                )}
              </Reorder.Group>

              <div className="mt-12 flex justify-between">
                <button data-testid="ob-back-3" onClick={() => setStep(2)}
                  className="text-[#737373] hover:text-[#1A1818] text-sm">← Back</button>
                <button data-testid="ob-finish" onClick={finish} disabled={saving}
                  className="inline-flex items-center gap-2 bg-[#913831] hover:bg-[#7A2E28] disabled:opacity-50 text-white rounded-full px-7 py-3.5 font-medium">
                  {saving && <Loader2 size={16} className="animate-spin" />}
                  Enter rendezvous
                </button>
              </div>
            </section>
          )}
        </motion.div>
      </main>
    </div>
  );
}
